import { Add } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import '../components/styles/ListPage.css'

interface ManufacturerOption {
  id: number
  provider: string
}

interface ManufacturerRowDraft {
  id: string
  manufacturerId: number | null
  provider: string
  hasLoa: boolean
  loaLink: string
}

export interface SupplierManufacturersDrawerProps {
  open: boolean
  onClose: () => void
  supplierId: number | null
  pendingOems?: string[]
}

export function SupplierManufacturersDrawer({
  open,
  onClose,
  supplierId,
  pendingOems,
}: SupplierManufacturersDrawerProps) {
  const [rows, setRows] = useState<ManufacturerRowDraft[]>([])
  const [options, setOptions] = useState<ManufacturerOption[]>([])
  const [deleteTarget, setDeleteTarget] = useState<ManufacturerRowDraft | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return

    if (pendingOems !== undefined) {
      setRows(
        [...pendingOems]
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
          .map(name => ({
            id: crypto.randomUUID(),
            manufacturerId: null,
            provider: name,
            hasLoa: false,
            loaLink: '',
          })),
      )
      return
    }

    if (supplierId === null) return

    setRows([])
    setLoading(true)

    Promise.all([
      axios.get<ManufacturerOption[]>(`/supplier/${supplierId}/manufacturers`),
      axios.get<{ count: number; list: ManufacturerOption[] }>('/manufacturer'),
    ])
      .then(([currentRes, optionsRes]) => {
        setRows(
          [...currentRes.data]
            .sort((a, b) => a.provider.localeCompare(b.provider, undefined, { sensitivity: 'base' }))
            .map(m => ({
              id: crypto.randomUUID(),
              manufacturerId: m.id,
              provider: m.provider,
              hasLoa: false,
              loaLink: '',
            })),
        )
        setOptions(optionsRes.data.list)
        setLoading(false)
      })
      .catch(err => {
        console.error('Failed to load supplier manufacturers', err)
        setLoading(false)
      })
  }, [open, supplierId, pendingOems])

  const handleAddNew = () => {
    setRows(prev => [
      { id: crypto.randomUUID(), manufacturerId: null, provider: '', hasLoa: false, loaLink: '' },
      ...prev,
    ])
  }

  const save = async (current: ManufacturerRowDraft[]) => {
    if (supplierId === null) return
    const sorted = [...current].sort((a, b) => a.provider.localeCompare(b.provider, undefined, { sensitivity: 'base' }))
    setRows(sorted)
    const manufacturerIds = sorted.map(r => r.manufacturerId).filter((id): id is number => id !== null)
    try {
      await axios.post(`/supplier/${supplierId}/manufacturers`, { manufacturerIds })
    } catch (err) {
      console.error('Failed to update supplier manufacturers', err)
    }
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    const updated = rows.filter(r => r.id !== deleteTarget.id)
    save(updated)
    setDeleteTarget(null)
  }

  return (
    <>
      <Drawer className="side-drawer" anchor="right" open={open} onClose={onClose}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {pendingOems !== undefined ? 'Requested OEM' : 'Authorized OEM'}
            </Typography>
            {pendingOems === undefined && (
              <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAddNew}>
                ADD NEW
              </Button>
            )}
          </Box>

          <IconButton onClick={onClose} size="small" aria-label="Close authorized OEM drawer">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{ borderRadius: 1, border: '1px solid rgba(0,0,0,0.12)' }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: '#3b5685',
                      '& th': { color: '#fff', fontWeight: 600, bgcolor: '#3b5685' },
                    }}
                  >
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>LOA</TableCell>
                    <TableCell>LOA Link</TableCell>
                    {pendingOems === undefined && <TableCell sx={{ width: 56 }} />}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={pendingOems !== undefined ? 3 : 4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {pendingOems !== undefined
                            ? 'No OEMs were requested.'
                            : 'No authorized OEMs. Click "+ ADD NEW" to add one.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(row => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          {pendingOems !== undefined ? (
                            <Typography variant="body2">{row.provider}</Typography>
                          ) : (
                            <Autocomplete
                              size="small"
                              options={options.filter(
                                o => !rows.some(r => r.manufacturerId === o.id && r.id !== row.id),
                              )}
                              getOptionLabel={o => o.provider}
                              value={options.find(o => o.id === row.manufacturerId) ?? null}
                              onChange={(_e, newValue) => {
                                const updated = {
                                  ...row,
                                  manufacturerId: newValue?.id ?? null,
                                  provider: newValue?.provider ?? '',
                                }
                                const newRows = rows.map(r => (r.id === row.id ? updated : r))
                                if (newValue) save(newRows)
                                else setRows(newRows)
                              }}
                              renderInput={params => <TextField {...params} placeholder="Manufacturer" />}
                            />
                          )}
                        </TableCell>

                        <TableCell>
                          <Checkbox size="small" disabled checked={row.hasLoa} />
                        </TableCell>

                        <TableCell>
                          <Link component="button" variant="body2" underline="hover" disabled>
                            Link
                          </Link>
                        </TableCell>

                        {pendingOems === undefined && (
                          <TableCell>
                            <IconButton size="small" aria-label="Delete OEM row" onClick={() => setDeleteTarget(row)}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Drawer>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Authorized OEM?"
        body="Are you sure you want to delete this authorized OEM? The action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  )
}
